import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Title } from "capitalrx-components-2.0/lib/typography";

import { GracePeriodStatus, ParticipationStatus, ThemedButton } from "@/App/components";
import { useIsParticipatingInMedicarePrescriptionPlan, useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { MemberEligibilityContext } from "@/App/context";

export const MedicarePrescriptionPaymentPlan: FC = () => {
  const { t: translate } = useTranslation();

  const navigate = useNavigateClient();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const firstOpenPlanYear = useMemo(() => {
      return (memberEligibility.mppp_plan_year_bounds || []).find((x) => x.is_open)?.plan_year;
  }, [memberEligibility.mppp_plan_year_bounds]);

  const { isParticipating } = useIsParticipatingInMedicarePrescriptionPlan(firstOpenPlanYear);

  const handleClickSeeParticipation = () => {
    logEvent(ANALYTIC_EVENTS.MPPP.MY_RX.optIn);
    navigate("/medicare-prescription-payment-plan?tab=participation-" + firstOpenPlanYear);
  };

  const handleClickDetails = () => {
    logEvent(ANALYTIC_EVENTS.MPPP.MY_RX.learnMore);
    navigate("/medicare-prescription-payment-plan?tab=calculator");
  };

  const { shouldStack } = useResponsive({
    shouldStack: {
      mobile: true,
      tablet: false,
      desktop: false,
    },
  });

  return (
    <Stack>
      <Title>{translate("BENEFITS.medicarePrescriptionPaymentPlan.medicarePrescriptionPaymentPlan")}</Title>
      <Body>{translate("BENEFITS.medicarePrescriptionPaymentPlan.medicarePrescriptionPaymentPlanDescription")}</Body>
      <Stack spacing="large">
        <Stack spacing="mini">
          {shouldStack ? (
            <Stack spacing="mini" alignment="left">
              <Body>{translate("BENEFITS.medicarePrescriptionPaymentPlan.participationStatus")}:</Body>
              <ParticipationStatus planYear={firstOpenPlanYear} />
              <GracePeriodStatus planYear={firstOpenPlanYear} />
            </Stack>
          ) : (
            <Shelf testId="participation-status" spacing="mini">
              <Body>{translate("BENEFITS.medicarePrescriptionPaymentPlan.participationStatus")}:</Body>
              <ParticipationStatus planYear={firstOpenPlanYear} />
              <GracePeriodStatus planYear={firstOpenPlanYear} />
              <Shelf.Spacer />
            </Shelf>
          )}
        </Stack>
        {shouldStack ? (
          <Stack spacing="small">
            <ThemedButton testId="learn-more" appearance="secondary" onClick={handleClickDetails}>
              {translate("BENEFITS.medicarePrescriptionPaymentPlan.learnMore")}
            </ThemedButton>
            {!isParticipating && (
              <ThemedButton testId="opt-in" appearance="secondary" onClick={handleClickSeeParticipation}>
                {translate("BENEFITS.medicarePrescriptionPaymentPlan.optIn")}
              </ThemedButton>
            )}
            {isParticipating && (
              <ThemedButton testId="participation-details" appearance="secondary" onClick={handleClickSeeParticipation}>
                {translate("BENEFITS.medicarePrescriptionPaymentPlan.participationDetails")}
              </ThemedButton>
            )}
          </Stack>
        ) : (
          <Shelf spacing="small">
            <ThemedButton testId="learn-more" appearance="secondary" onClick={handleClickDetails}>
              {translate("BENEFITS.medicarePrescriptionPaymentPlan.learnMore")}
            </ThemedButton>
            {!isParticipating && (
              <ThemedButton testId="opt-in" appearance="secondary" onClick={handleClickSeeParticipation}>
                {translate("BENEFITS.medicarePrescriptionPaymentPlan.optIn")}
              </ThemedButton>
            )}
            {isParticipating && (
              <ThemedButton testId="participation-details" appearance="secondary" onClick={handleClickSeeParticipation}>
                {translate("BENEFITS.medicarePrescriptionPaymentPlan.participationDetails")}
              </ThemedButton>
            )}
            <Shelf.Spacer />
          </Shelf>
        )}
      </Stack>
    </Stack>
  );
};
